import React, { CSSProperties } from "react";

import { cn } from "@oneleet/utils";

import { Highlight, PrismTheme, themes } from "prism-react-renderer";
import { twMerge } from "tailwind-merge";

import { CopyButton } from "../../../..";

const theme = themes.nightOwl;

type NgCodeBlockCustomTheme = CSSProperties;
type PrismThemeName = keyof typeof themes;
interface CodeBlockProps {
  language?: string;
  content: string;
  copyable?: boolean;
  withLineDecorators?: boolean;
  className?: string;
  startLineNumber?: number;
  customTheme?: NgCodeBlockCustomTheme;
  prismTheme?: PrismThemeName;
  classNames?: {
    root?: string;
    pre?: string;
    code?: string;
    lineNumber?: string;
    line?: string;
  };
}

const NgCodeBlock: React.FC<CodeBlockProps> = ({
  language = "text",
  content,
  copyable = true,
  withLineDecorators = false,
  className,
  startLineNumber,
  customTheme,
  prismTheme,
  classNames,
}) => {
  const filteredCustomTheme = Object.fromEntries(
    Object.entries(customTheme ?? {}).filter(
      ([_, value]) => typeof value === "string",
    ),
  ) as Partial<PrismTheme["plain"]>;

  const displayTheme = prismTheme ? (themes[prismTheme] ?? theme) : theme;
  const codeBlockTheme = {
    ...displayTheme,
    plain: {
      ...displayTheme.plain,
      ...filteredCustomTheme,
    },
  };
  return (
    <div className={twMerge("relative", className, classNames?.root)}>
      <Highlight
        theme={codeBlockTheme}
        code={content ? content.trim() : ""}
        language={language}
      >
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <pre
            className={cn(
              className,
              "text-ng-sm flex items-start gap-2 rounded-md p-4",
              classNames?.pre,
            )}
            style={style}
          >
            <code
              className={cn(
                "text-ng-sm grow self-center overflow-x-auto",
                classNames?.code,
              )}
            >
              {tokens.map((line, i) => (
                <div
                  key={i}
                  {...getLineProps({ line })}
                  className={cn(classNames?.line)}
                >
                  {withLineDecorators && (
                    <span
                      className={cn(
                        "select-none pr-4 opacity-50",
                        classNames?.lineNumber,
                      )}
                    >
                      {language === "bash" && line[0]?.content.trim()
                        ? "$"
                        : language !== "bash"
                          ? startLineNumber
                            ? startLineNumber + i
                            : i + 1
                          : ""}
                    </span>
                  )}
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token })} />
                  ))}
                </div>
              ))}
            </code>
            {copyable && <CopyButton content={content} />}
          </pre>
        )}
      </Highlight>
    </div>
  );
};

export default NgCodeBlock;
